import Select from "react-select"
import React, { FormEvent, useEffect, useMemo, useState } from "react"
import { SelectOption } from "../CategorieClient/List"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"
import { People } from "../../types/People"
import { WorkflowValidation } from "../../types/WorklowValidation"

interface Props
{
    dealerships: SelectOption[]
    users: People[]
    workflow?: WorkflowValidation
    isEdition?: boolean
    setSelectedWorkflow: (workflow?: WorkflowValidation) => void
    fetchWorkflows?: () => void
    workflows?: WorkflowValidation[]
}

const defaultWorkflow: WorkflowValidation = {
    IDSite: undefined,
    EditionOffre: false,
    EditionCommande: false,
    EditionOffrePeopleIds: [],
    EditionCommandePeopleIds: [],
}

export default function ModalCreationWorkflow(props: Props) {

    const backofficeApiClient = new BackOfficeAPIClient()

    const [ workflow, setWorkflow ] = useState<WorkflowValidation>(defaultWorkflow)

    useEffect(() => {
        if (props.isEdition && props.workflow) {
            setWorkflow(props.workflow)
        } else {
            setWorkflow(defaultWorkflow)
        }
    }, [ props.workflow, props.isEdition ])

    const submitWorkflow = async (e: FormEvent) => {
        e.preventDefault()
        try {
            if (props.isEdition) {
                await editWorkflow()
            } else {
                await addWorkflow()
            }
        } catch {
            throw new Error(`Erreur lors de la ${props.isEdition ? "modification" : "sauvegarde"} du workflow`)
        } finally {
            document.getElementById("closeButtonCreateWorkflowModal")?.click()
        }

    }

    const addWorkflow = async () => {
        await backofficeApiClient.post("/workflowsValidation", workflow).then(() => {
            if (props.fetchWorkflows) {
                props.fetchWorkflows()
            }
        })
    }

    const editWorkflow = async () => {
        await backofficeApiClient.put("/workflowsValidation", workflow).then(() => {
            if (props.fetchWorkflows) {
                props.fetchWorkflows()
            }
        })
    }

    const resetWorkflow = () => {
        setWorkflow(defaultWorkflow)
        props.setSelectedWorkflow(undefined)
    }

    const usersOptions: SelectOption[] = useMemo(() => {
        let users = props.users
        if (workflow.IDSite) {
            users = users.filter(user =>
                user.Sites?.some(site => site.IDSite === workflow.IDSite),
            )
        }

        return users.map((user: People) => {
            return {
                label: `${user.Name} (${user.DRProfileEsellerLead_Libelle})`,
                value: user.IDPeople.toString(),
            } as SelectOption
        })
    }, [ props.users, workflow.IDSite ])

    const dealershipsOptions = useMemo(() => {
        const usedDealershipIds = props.workflows?.map(wf => wf.IDSite) ?? []

        return props.dealerships?.filter(dealership => {
            const id = Number(dealership.value)
            //On garde les sites non utilisés OU le site déjà sélectionné
            return !usedDealershipIds.includes(id) || id === workflow.IDSite || id === props.workflow?.IDSite
        })
    }, [ props.workflows, props.dealerships, workflow.IDSite ])

    return (
        <div data-backdrop="static" className="modal fade" id="createModal" role="dialog" aria-labelledby="createModalLabel" aria-hidden="true">
            <form onSubmit={(e) => submitWorkflow(e)} id="createWorkflow" className={"tw-h-full"}>
                <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="createModalLabel">{props.isEdition ? "Modifier" : "Ajouter"} un workflow</h5>
                            <button onClick={resetWorkflow} type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className={"tw-flex tw-flex-row tw-items-center tw-gap-4"} style={{ zIndex: 10 }}>
                                <span className={"tw-w/1/3"} style={{ fontSize: '.9em' }}>Site concerné</span>
                                <Select
                                    required
                                    className={"tw-w-2/3"}
                                    placeholder="Aucun site sélectionné"
                                    multi={false}
                                    closeOnSelect={true}
                                    options={dealershipsOptions}
                                    value={props.dealerships.find(d => Number(d.value) === workflow.IDSite) || null}
                                    onChange={(dealership: SelectOption) => setWorkflow({
                                        ...workflow,
                                        IDSite: Number(dealership.value),
                                        EditionCommandePeopleIds: [], // On remet a zero car les utilisateurs peuvent ne plus être sur le site
                                        EditionOffrePeopleIds: [], // On remet a zero car les utilisateurs peuvent ne plus être sur le site
                                    })}
                                />
                            </div>
                            <div className={"tw-mt-8"}>
                                <span className={"tw-font-semibold"}>Actions nécessitant validation : </span>
                            </div>
                            <div className={"tw-mt-8 tw-space-y-4"}>
                                <div className={"tw-flex tw-flex-row tw-items-center tw-justify-between"}>
                                    <div className={"tw-flex tw-flex-row tw-items-center tw-gap-4 tw-w-1/4"}>
                                        <input
                                            type="checkbox"
                                            id="editOffer"
                                            checked={workflow.EditionOffre}
                                            onChange={(e) => setWorkflow({ ...workflow, EditionOffre: e.target.checked })}
                                        />
                                        <span>Editer une offre</span>
                                    </div>
                                    <div className={"tw-flex tw-w-2/3 tw-items-center tw-gap-4"}>
                                        <span>Définir les validateurs : </span>
                                        <Select
                                            disabled={!workflow.EditionOffre}
                                            className={"tw-w-2/3"}
                                            placeholder="Aucun utilisateur sélectionné"
                                            multi={true}
                                            closeOnSelect={false}
                                            options={usersOptions}
                                            value={usersOptions.filter(u => workflow.EditionOffrePeopleIds.includes(Number(u.value)))}
                                            onChange={(users: SelectOption[]) =>
                                                setWorkflow({
                                                    ...workflow,
                                                    EditionOffrePeopleIds: users.map(u => Number(u.value)),
                                                })
                                            }
                                            required={workflow.EditionOffre}
                                        />
                                    </div>
                                </div>
                                <div className={"tw-flex tw-flex-row tw-items-center tw-justify-between"}>
                                    <div className={"tw-flex tw-flex-row tw-items-center tw-gap-4 tw-w-1/4"}>
                                        <input
                                            type="checkbox"
                                            id="editOrder"
                                            checked={workflow.EditionCommande}
                                            onChange={(e) => setWorkflow({ ...workflow, EditionCommande: e.target.checked })}
                                        />
                                        <span>Editer une commande</span>
                                    </div>
                                    <div className={"tw-flex tw-w-2/3 tw-items-center tw-gap-4"}>
                                        <span>Définir les validateurs : </span>
                                        <Select
                                            disabled={!workflow.EditionCommande}
                                            className={"tw-w-2/3"}
                                            placeholder="Aucun utilisateur sélectionné"
                                            multi={true}
                                            closeOnSelect={false}
                                            options={usersOptions}
                                            value={usersOptions.filter(u => workflow.EditionCommandePeopleIds.includes(Number(u.value)))}
                                            onChange={(users: SelectOption[]) =>
                                                setWorkflow({
                                                    ...workflow,
                                                    EditionCommandePeopleIds: users.map(u => Number(u.value)),
                                                })
                                            }
                                            required={workflow.EditionCommande}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button onClick={resetWorkflow} id="closeButtonCreateWorkflowModal" type="button" className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <input value={props.isEdition ? "Modifier" : "Sauvegarder"} type="submit" className="btn btn-primary"/>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}
